/**
 * As três prateleiras de cinema do painel: o `cinema_preview.dart`, o
 * `currently_watching_preview.dart` e o `anime_preview.dart`.
 *
 * Ficam juntas neste arquivo porque são a mesma prateleira três vezes — muda o
 * filtro, o título e o que vai escrito embaixo de cada ficha. Três cópias do
 * mesmo `map` divergiriam na primeira correção.
 *
 * As palavras da legenda são as de lá, em inglês (`'curated together'`,
 * `'N titles • shared'`): estão assim no Flutter e o porte não traduz o que o
 * outro app mostra.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Uma fileira, não uma por pessoa.** Lá o `_CinemaShelf` se parte em "ME" e
 *   o par, porque lá a casa é sempre de duas pessoas. Aqui a casa tem quantas
 *   quiser, e quem viu vira o nome miúdo em cima do título — a mesma
 *   atribuição, num lugar que não cresce com a casa.
 * - **Sem gaveta.** O toque de cada ficha lá abre o `EpisodeDrawer`, uma folha
 *   que sobe. No site o cinema tem tela própria com essa gaveta dentro, então o
 *   toque da seção inteira leva a `/cinema` e as fichas saem sem clique — um
 *   `<button>` dentro de um `<a>` é HTML inválido.
 * - **A setinha de "Assistindo agora" leva a algum lugar.** Lá o `onViewAll`
 *   dela é `() {}`, uma setinha que não faz nada. Uma seção com cara de
 *   clicável que não abre nada é pior do que a divergência.
 * - **Dez, e não a prateleira toda.** O `ShelfMarquee` desliza sozinho e pode
 *   carregar tudo; aqui a fileira é de rolar com o dedo.
 * - **Sem as escolhas do Kovu aqui.** O `cinema_preview.dart` põe um segundo
 *   `AIRecommendations` entre o cabeçalho e a prateleira. O porte dele existe e
 *   é o mesmo widget: `telas/cinema/escolhas.tsx`, na aba Início do cinema, que
 *   é onde o `cinema_home_tab.dart` também o põe. Repetir aqui custaria uma
 *   pergunta ao Kovu e até cinco buscas no TMDB toda vez que o painel abre, e o
 *   painel não tem o `ContextoTMDB` de pé — quem quer as escolhas está a um
 *   clique na seção.
 * - **A setinha do anime leva a `/anime`, como lá.** É o `onViewAll` do
 *   `anime_preview.dart` (`context.push('/anime')`) apontando para a casa
 *   própria do AnimeX, que agora existe: `telas/anime/`. Esta prateleira é a
 *   vitrine dela no painel.
 * - **O anime aparece duas vezes, e é assim mesmo.** O
 *   `getAnimeWatchListStream` de lá é um filtro por cima da mesma lista que o
 *   `getWatchListStream` devolve — nada é tirado de "Vistos" por ser anime. Um
 *   anime visto está nas duas prateleiras no Flutter e está nas duas aqui.
 */

import { useMemo } from 'react';
import {
  COLECAO_CINEMA,
  ehFilme,
  estaAssistindo,
  estaVisto,
  posterDoDoc,
  rotuloDeQuemQuer,
  traduzir,
  usarColecao,
  usarSessao,
  type DocMidia,
} from '@nosso-sempre/shared';
import { FichaDaEstante } from './FichaDaEstante';
import { Icone } from './Icone';
import { Secao, SetaDaSecao } from './Secao';
import './previas.css';

/** Quantos cabem antes de a setinha ser o caminho mais curto. */
const QUANTOS = 10;

/** O `ShelfHeader.itemCount`, com o pontinho no meio como lá. */
function legendaDaEstante(quantos: number): string {
  if (quantos === 0) return 'curated together';
  return `${quantos} ${quantos === 1 ? 'title' : 'titles'} • shared`;
}

/**
 * A lista de um filtro, da mais recente para a mais antiga.
 *
 * O `getWatchListStream` de lá pede a lista de uma pessoa; aqui a casa inteira,
 * porque a fileira é uma só e quem viu vai escrito na ficha.
 */
function usarFichas(passa: (status: string) => boolean, soAnime = false) {
  const documentos = usarColecao<DocMidia>(COLECAO_CINEMA);
  return useMemo(
    () =>
      documentos
        .filter((d) => passa(d.dados.status ?? '') && (!soAnime || d.dados.isAnime))
        .sort((a, b) => (b.dados.addedAt ?? '').localeCompare(a.dados.addedAt ?? ''))
        .slice(0, QUANTOS),
    [documentos, passa, soAnime],
  );
}

/** O `ShelfMarquee` com as fichas dentro, ou o `ShelfEmpty` no lugar delas. */
function Estante({
  fichas,
  vazio,
  abaixoDe,
  seloDe,
  nomeDe,
}: {
  fichas: ReadonlyArray<{ id: string; dados: DocMidia }>;
  vazio: string;
  abaixoDe: (d: DocMidia) => string;
  seloDe: (d: DocMidia) => string | undefined;
  nomeDe: (d: DocMidia) => string;
}) {
  const { casa } = usarSessao();

  if (fichas.length === 0) {
    return (
      <p className="previa-convite">
        <Icone nome="mais-circulo" tamanho={18} />
        {vazio}
      </p>
    );
  }

  return (
    <ul className="previa-estante">
      {fichas.map(({ id, dados }) => (
        <li
          key={id}
          // O `accent` por ficha do `currently_watching_preview`: anime é
          // lavanda, o resto segue a cor da seção.
          style={dados.isAnime ? { ['--cor-secao' as string]: 'var(--lavanda)' } : undefined}
        >
          <FichaDaEstante
            capa={posterDoDoc(dados.posterPath ?? '')}
            icone="cinema"
            titulo={nomeDe(dados)}
            acima={rotuloDeQuemQuer(dados.userName ?? '', casa)}
            abaixo={abaixoDe(dados)}
            selo={seloDe(dados)}
          />
        </li>
      ))}
    </ul>
  );
}

/** O `_subtitleFor` do `_CinemaShelf`: o ano e o tipo. */
function anoETipo(d: DocMidia): string {
  const partes: string[] = [];
  if (d.year) partes.push(d.year);
  if (d.mediaType) partes.push(d.mediaType === 'tv' ? 'Series' : 'Movie');
  return partes.join(' • ');
}

export function PreviaDoCinema() {
  const fichas = usarFichas(estaVisto);

  return (
    <Secao
      icone="cinema"
      cor="var(--rosa-quartzo)"
      titulo={traduzir('Vistos')}
      legenda={legendaDaEstante(fichas.length)}
      para="/cinema"
      ponta={<SetaDaSecao />}
    >
      <Estante
        fichas={fichas}
        vazio={traduzir('Nenhum filme visto ainda. Começa uma sessão!')}
        nomeDe={(d) => d.title ?? ''}
        abaixoDe={anoETipo}
        seloDe={(d) => ((d.mediaType ?? '').toUpperCase() === 'TV' ? 'TV' : undefined)}
      />
    </Secao>
  );
}

/** O episódio em que a série parou: `T2E7`, o mesmo do selo e da legenda. */
function episodio(d: DocMidia): string | undefined {
  if (d.currentEpisode == null) return undefined;
  return `T${d.currentSeason ?? 1}E${d.currentEpisode}`;
}

/** O `_subtitleFor` do `_CurrentlyWatchingShelf`: filme tem ano, série tem episódio. */
function ondeParou(d: DocMidia): string {
  const partes: string[] = [];
  if (ehFilme(d.mediaType ?? '')) {
    if (d.year) partes.push(d.year);
    partes.push('Movie');
  } else {
    const ep = episodio(d);
    if (ep) partes.push(ep);
    if (d.year) partes.push(d.year);
  }
  return partes.join(' • ');
}

/**
 * O `_sanitizeTitle`: alguns títulos vieram de raspagem com classe de CSS
 * grudada no meio (`css-1dbjc4n`). Tira, junta os espaços que sobraram, e se
 * não sobrar nada devolve o título cru — melhor um título feio do que nenhum.
 */
const LIXO_DE_CSS = /\bcss-[a-zA-Z0-9_-]+\b/g;

function tituloLimpo(bruto: string): string {
  const limpo = bruto
    .replace(LIXO_DE_CSS, ' ')
    .trim()
    .replace(/\s{2,}/g, ' ');
  return limpo || bruto;
}

export function PreviaDoQueEstamosVendo() {
  const fichas = usarFichas(estaAssistindo);

  return (
    <Secao
      icone="tocar-circulo"
      cor="var(--rosa-quartzo)"
      titulo={traduzir('Assistindo agora')}
      legenda={legendaDaEstante(fichas.length)}
      para="/cinema"
      ponta={<SetaDaSecao />}
    >
      <Estante
        fichas={fichas}
        vazio={traduzir('Nada rolando agora. Começa um filme ou uma série!')}
        nomeDe={(d) => tituloLimpo(d.title ?? '')}
        abaixoDe={ondeParou}
        seloDe={(d) => (ehFilme(d.mediaType ?? '') ? undefined : episodio(d))}
      />
    </Secao>
  );
}

/**
 * O `_subtitleFor` do `_AnimeShelf`: o estúdio, ou o formato, ou o ano — o
 * primeiro que tiver alguma coisa escrita, não os três emendados.
 */
function estudioOuAno(d: DocMidia): string {
  if (d.studio) return d.studio;
  if (d.format) return d.format;
  return d.year ?? '';
}

export function PreviaDoAnime() {
  const fichas = usarFichas(estaVisto, true);

  return (
    <Secao
      icone="cinema"
      cor="var(--lavanda)"
      titulo="Anime"
      /* O selo do `ShelfHeader` aparece quando a prateleira está VAZIA — é um
         convite, não um troféu (`badge: _items.isNotEmpty ? null : 'NOVO'`). */
      marca={fichas.length === 0 ? 'NOVO' : undefined}
      legenda={legendaDaEstante(fichas.length)}
      /* `anime_preview.dart`: `onViewAll: () => context.push('/anime')`. */
      para="/anime"
      ponta={<SetaDaSecao />}
    >
      <Estante
        fichas={fichas}
        vazio={traduzir('Nenhum anime visto ainda. Bora maratonar!')}
        nomeDe={(d) => tituloLimpo(d.title ?? '')}
        abaixoDe={estudioOuAno}
        seloDe={() => undefined}
      />
    </Secao>
  );
}
